import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { SiteConfigService } from '../config/site-config.service';

export interface SeoData {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  robots?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private config = inject(SiteConfigService);

  private get siteName() { return this.config.seo.siteName; }
  private get siteUrl() { return this.config.seo.siteUrl; }
  private get defaultDescription() { return this.config.seo.defaultDescription; }
  private get defaultKeywords() { return this.config.seo.defaultKeywords; }
  private get defaultImage() { return this.config.seo.ogImage; }
  private get contact() { return this.config.contact; }
  private get schemaType() { return this.config.seo.schemaType; }

  constructor(
    private title: Title,
    private meta: Meta,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  /** Call once in AppComponent to auto-update SEO from route data */
  init(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        mergeMap(route => route.data)
      )
      .subscribe(data => {
        if (data['seo']) {
          this.update(data['seo']);
        }
      });
  }

  /** Update all SEO tags at once */
  update(data: SeoData): void {
    const pageTitle = data.title
      ? `${data.title} | ${this.siteName}`
      : this.siteName;
    this.title.setTitle(pageTitle);

    const description = data.description || this.defaultDescription;
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords', content: data.keywords || this.defaultKeywords });
    this.meta.updateTag({ name: 'robots', content: data.robots || 'index, follow' });

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: data.title || this.siteName });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: data.ogType || 'website' });
    this.meta.updateTag({ property: 'og:image', content: data.ogImage || this.defaultImage });
    this.meta.updateTag({ property: 'og:site_name', content: this.siteName });

    // Twitter Card
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: data.title || this.siteName });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: data.ogImage || this.defaultImage });

    // Canonical & OG URL
    const canonical = data.canonical || this.getCurrentUrl();
    this.updateCanonical(canonical);
    this.meta.updateTag({ property: 'og:url', content: canonical });

    // JSON-LD
    if (data.jsonLd) {
      this.setJsonLd(data.jsonLd);
    }
  }

  /** Set page title (appends site name) */
  setTitle(pageTitle: string): void {
    this.title.setTitle(`${pageTitle} | ${this.siteName}`);
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
  }

  /** Set meta description */
  setDescription(description: string): void {
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ name: 'twitter:description', content: description });
  }

  /** Update or create canonical link */
  private updateCanonical(url: string): void {
    let link: HTMLLinkElement | null = this.doc.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  /** Inject JSON-LD structured data */
  setJsonLd(data: Record<string, unknown> | Record<string, unknown>[]): void {
    const existing = this.doc.querySelectorAll('script[type="application/ld+json"][data-seo]');
    existing.forEach(el => el.remove());

    const script = this.doc.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(data);
    this.doc.head.appendChild(script);
  }

  /** Get the organization schema */
  getOrganizationSchema(): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': this.siteName,
      'url': this.siteUrl,
      'logo': `${this.siteUrl}/assets/logo.png`,
      'description': this.defaultDescription,
      'telephone': this.contact.phone,
      'email': this.contact.email,
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': this.contact.address.street,
        'addressLocality': this.contact.address.city,
        'addressRegion': this.contact.address.state,
        'postalCode': this.contact.address.zip,
        'addressCountry': 'US',
      },
    };
  }

  /** Get the local business schema */
  getLocalBusinessSchema(): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': this.schemaType,
      'name': this.siteName,
      'url': this.siteUrl,
      'image': this.defaultImage,
      'telephone': this.contact.phone,
      'email': this.contact.email,
      'priceRange': '$$',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': this.contact.address.street,
        'addressLocality': this.contact.address.city,
        'addressRegion': this.contact.address.state,
        'postalCode': this.contact.address.zip,
        'addressCountry': 'US',
      },
      'geo': {
        '@type': 'GeoCoordinates',
        'latitude': this.contact.coordinates.lat,
        'longitude': this.contact.coordinates.lng,
      },
      'openingHoursSpecification': [
        { '@type': 'OpeningHoursSpecification', 'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], 'opens': '07:00', 'closes': '17:00' },
        { '@type': 'OpeningHoursSpecification', 'dayOfWeek': 'Saturday', 'opens': '08:00', 'closes': '14:00' },
      ],
      'areaServed': this.contact.serviceAreas.map(area => ({
        '@type': 'City',
        'name': area,
      })),
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '4.9',
        'reviewCount': '200',
        'bestRating': '5',
      },
    };
  }

  /** Build a BreadcrumbList schema */
  getBreadcrumbSchema(items: { name: string; url: string }[]): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': items.map((item, i) => ({
        '@type': 'ListItem',
        'position': i + 1,
        'name': item.name,
        'item': `${this.siteUrl}${item.url}`,
      })),
    };
  }

  /** Build a Service schema */
  getServiceSchema(name: string, description: string): Record<string, unknown> {
    return {
      '@context': 'https://schema.org',
      '@type': 'Service',
      'name': name,
      'description': description,
      'provider': {
        '@type': 'Organization',
        'name': this.siteName,
      },
      'areaServed': {
        '@type': 'State',
        'name': 'Florida',
      },
    };
  }

  private getCurrentUrl(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.href;
    }
    return `${this.siteUrl}${this.router.url}`;
  }
}
